import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '@/store/AppContext';
import moduleRegistry from '@/config/moduleRegistry';
import { ImageStyle } from '@/types';
import { Camera, Palette, BarChart3, Sparkles, Package, Monitor } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import type { TranslationKey } from '@/i18n/translations';
import { getAppPathname, matchAppPath } from '@/i18n/localeRouting';
import { useAppNavigate } from '@/hooks/useAppNavigate';

interface ModuleSelectorProps {
  variant?: 'default' | 'header' | 'sidebar';
}

const moduleShortLabelKeys: Record<string, TranslationKey> = {
  packshot: 'module.short.packshot',
  photo: 'module.short.photo',
  mockup: 'module.short.mockup',
  infographic: 'module.short.infographic',
  illustration: 'module.short.illustration',
  icon: 'module.short.illustration',
};

const moduleNameKeys: Record<string, TranslationKey> = {
  packshot: 'module.packshot',
  photo: 'module.photo',
  mockup: 'module.mockup',
  infographic: 'module.infographic',
  illustration: 'module.illustration',
};

export default function ModuleSelector({ variant = 'default' }: ModuleSelectorProps) {
  const { activeModule, setActiveModule, isGenerating } = useApp();
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentImageStyle = searchParams.get('imageStyle');
  const appPath = getAppPathname(location.pathname);
  const isOnCreate = matchAppPath(location.pathname, '/create');
  const isSessionRoute = /^\/create\/[^/]+$/.test(appPath);

  const getIcon = (moduleId: string, sizeClass = 'w-3.5 h-3.5') => {
    switch (moduleId) {
      case 'photo':
        return <Camera className={sizeClass} strokeWidth={2} />;
      case 'illustration':
        return <Palette className={sizeClass} strokeWidth={2} />;
      case 'infographic':
        return <BarChart3 className={sizeClass} strokeWidth={2} />;
      case 'icon':
        return <Sparkles className={sizeClass} strokeWidth={2} />;
      case 'packshot':
        return <Package className={sizeClass} strokeWidth={2} />;
      case 'mockup':
        return <Monitor className={sizeClass} strokeWidth={2} />;
      default:
        return null;
    }
  };

  const getModuleLabel = (moduleId: string) => {
    const key = moduleNameKeys[moduleId];
    return key ? t(key) : moduleRegistry[moduleId]?.name || moduleId;
  };

  const getModuleShortLabel = (moduleId: string) => {
    const key = moduleShortLabelKeys[moduleId];
    return key ? t(key) : getModuleLabel(moduleId);
  };

  const isModuleActive = (moduleId: string) => {
    if (!isOnCreate) return false;
    if (currentImageStyle) return currentImageStyle === moduleId;
    if (isSessionRoute && moduleId === 'packshot') return true;
    return activeModule === moduleId;
  };

  const handleModuleChange = (moduleId: ImageStyle) => {
    if (isGenerating) return;

    setActiveModule(moduleId);

    if (isOnCreate) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('imageStyle', moduleId);

      if (moduleId === 'illustration') {
        newSearchParams.set('sceneType', 'action');
      }

      navigate(`/create?${newSearchParams.toString()}`, { replace: true });
      return;
    }

    const newSearchParams = new URLSearchParams();
    newSearchParams.set('imageStyle', moduleId);

    if (moduleId === 'illustration') {
      newSearchParams.set('sceneType', 'action');
    }

    navigate(`/create?${newSearchParams.toString()}`);
  };

  const modules = Object.values(moduleRegistry).filter((module) => !module.disabled);
  const isHeader = variant === 'header';
  const isSidebar = variant === 'sidebar';

  if (isSidebar) {
    return (
      <nav className="flex flex-col w-full">
        {modules.map((module) => {
          const isDisabled = isGenerating;
          const isActive = isModuleActive(module.id);
          return (
            <button
              key={module.id}
              type="button"
              title={getModuleLabel(module.id)}
              onClick={() => handleModuleChange(module.id as ImageStyle)}
              disabled={isDisabled}
              className={`w-full flex flex-col items-center gap-1 py-2.5 px-1 transition-all border-r-2 ${
                isDisabled
                  ? 'opacity-50 cursor-not-allowed text-text-disabled border-transparent'
                  : isActive
                  ? 'border-white text-text-primary opacity-100'
                  : 'border-transparent text-text-secondary opacity-80 hover:opacity-100 hover:text-text-primary'
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center">
                {getIcon(module.id, 'w-6 h-6')}
              </div>
              <span className="text-[11px] font-medium leading-none text-center">
                {getModuleShortLabel(module.id)}
              </span>
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <div
      className={
        isHeader
          ? 'flex items-center gap-1 overflow-x-auto w-full min-w-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
          : 'flex gap-1 bg-bg-control rounded-full p-1 w-fit'
      }
    >
      {modules.map((module) => {
        const isDisabled = isGenerating;
        const isActive = isModuleActive(module.id);
        return (
          <button
            key={module.id}
            type="button"
            onClick={() => handleModuleChange(module.id as ImageStyle)}
            disabled={isDisabled}
            className={`${isHeader ? 'shrink-0' : ''} px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${
              isDisabled
                ? 'opacity-50 cursor-not-allowed text-text-disabled'
                : isActive
                ? 'bg-accent text-bg-main'
                : isHeader
                ? 'text-text-secondary hover:text-text-primary hover:bg-bg-control'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {getIcon(module.id)}
            <span>{getModuleLabel(module.id)}</span>
          </button>
        );
      })}
    </div>
  );
}
